import { useQuery } from "@tanstack/react-query";

import { EvidenceAPI } from "@/Features/Evidence/evidence.api";
import { FilesAPI } from "@/Features/Files/files.api";
import { FILE_SOURCE_TYPES } from "@/Features/Files/files.types";
import type { File } from "@/Features/Files/files.types";

import { evidenceFilesQueryKeys } from "./evidence-files.keys";

/**
 * The evidence item's folder under Evidence Files in Case Files, or null when
 * the item has no files yet (the first upload through the evidence photo
 * routes creates it). Reads the generic Files list, so it only resolves with
 * the evidence_files_in_case_files flag on: with it off the Files API hides
 * the whole managed tree. The evidence Files tab (ENG-2156) is the caller.
 */
export function useEvidenceItemFolder(
  { evidence_id, case_uuid }: { evidence_id?: number; case_uuid?: string },
  options?: { enabled?: boolean }
) {
  return useQuery<File | null>({
    queryKey: evidenceFilesQueryKeys.itemFolder(evidence_id),
    queryFn: async () => {
      const res = await FilesAPI.get({
        case_uuid,
        source_type: FILE_SOURCE_TYPES.EVIDENCE_ITEM_FOLDER,
        source_id: evidence_id,
      });
      if (
        (res as { success?: boolean })?.success === false ||
        !Array.isArray(res?.data)
      ) {
        throw new Error("Failed to find the evidence item folder");
      }
      return res.data.find((row) => row.is_folder) ?? null;
    },
    enabled: evidence_id != null && !!case_uuid && (options?.enabled ?? true),
    // The connection.js interceptor already toasts each failed attempt.
    retry: false,
  });
}

export type EvidenceItemSummary = {
  evidence_id: number;
  evidence_number: string;
  uuid: string;
  case_id: number;
};

export const evidenceItemSummaryQueryKey = (evidence_id?: number | null) => [
  // Under ["evidence", "list"] so the evidence mutations that invalidate the
  // lists (a renumbered item, a delete) refresh this too.
  "evidence",
  "list",
  { evidence_id, view: "item-summary" },
];

/**
 * The evidence number and uuid for a managed Case Files row's `source_id`
 * (ENG-2133). File rows only carry the numeric id, so the badges, banners,
 * modal alerts and flyout link look the item up here, cached per item. Pass
 * `enabled: false` to wait until the value is needed (a tooltip opening).
 * Callers fall back to "an evidence item" and hide links while it loads or
 * fails.
 */
export function useEvidenceItemSummary(
  evidence_id?: number | null,
  options?: { enabled?: boolean }
) {
  return useQuery<EvidenceItemSummary | null>({
    queryKey: evidenceItemSummaryQueryKey(evidence_id),
    queryFn: async () => {
      // Optional: a user who can see the case's files but not the item gets
      // the fallback copy, not an error toast.
      const res = await EvidenceAPI.get(
        { evidence_id: evidence_id! },
        { skipErrorToast: true }
      );
      if (
        (res as { success?: boolean })?.success === false ||
        !Array.isArray(res?.data)
      ) {
        throw new Error("Failed to load the evidence item");
      }
      const item = res.data[0];
      if (!item) return null;
      return {
        evidence_id: item.evidence_id,
        evidence_number: item.evidence_number,
        uuid: item.uuid,
        case_id: item.case_id,
      };
    },
    enabled: evidence_id != null && (options?.enabled ?? true),
    retry: false,
    // Short, so a renumbered item shows its new number on the next view: the
    // evidence edits invalidate their own list keys, not this one.
    staleTime: 30 * 1000,
  });
}
